import React, { useEffect, useState } from "react";
import ProviderSettingsSelector from "../ProviderSettingsSelector";
import { callDirectionOptions, personaTypeOptions } from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface TestSettingsPanelProps {
  lab: UseAgentTestLab;
}

interface NumberSettingInputProps {
  label: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
}

const NumberSettingInput: React.FC<NumberSettingInputProps> = ({
  label,
  min,
  max,
  value,
  onChange,
}) => {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={draftValue}
        onChange={(event) => {
          const nextValue = event.target.value;
          setDraftValue(nextValue);
          if (nextValue === "") return;

          const parsedValue = Number(nextValue);
          if (!Number.isNaN(parsedValue)) {
            onChange(parsedValue);
          }
        }}
        className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
      />
    </div>
  );
};

export const TestSettingsPanel: React.FC<TestSettingsPanelProps> = ({ lab }) => (
  <div className="space-y-6">
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-[var(--foreground)]">
          Providers
        </h4>
        <button
          type="button"
          onClick={lab.resetProvidersToDefaults}
          className="text-xs text-[var(--accent)] hover:underline"
        >
          Test Lab defaults
        </button>
      </div>
      <div className="space-y-3">
        <ProviderSettingsSelector
          title="Scenario generator"
          description="Creates stable personas and scenarios."
          providers={lab.llmProviders}
          selected={lab.scenarioProvider}
          onChange={lab.setScenarioProvider}
          defaultExpanded={false}
        />
        <ProviderSettingsSelector
          title="Agent under test"
          description="Generates assistant turns for the saved prompt."
          providers={lab.llmProviders}
          selected={lab.agentProvider}
          onChange={lab.setAgentProvider}
          defaultExpanded={false}
        />
        <ProviderSettingsSelector
          title="User simulator"
          description="Acts as the saved persona."
          providers={lab.llmProviders}
          selected={lab.userProvider}
          onChange={lab.setUserProvider}
          defaultExpanded={false}
        />
        <ProviderSettingsSelector
          title="Judge"
          description="Scores criteria and failure traps."
          providers={lab.llmProviders}
          selected={lab.judgeProvider}
          onChange={lab.setJudgeProvider}
          defaultExpanded={false}
        />
        <ProviderSettingsSelector
          title="Prompt optimizer"
          description="Creates shadow prompt candidates."
          providers={lab.llmProviders}
          selected={lab.optimizerProvider}
          onChange={lab.setOptimizerProvider}
          defaultExpanded={false}
        />
      </div>
    </section>

    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-medium text-[var(--foreground)]">
          Runtime fields
        </h4>
        <button
          type="button"
          onClick={lab.resetRuntimeFields}
          className="text-xs text-[var(--accent)] hover:underline"
        >
          Reset to agent defaults
        </button>
      </div>
      {Object.keys(lab.runtimeFields).length === 0 ? (
        <p className="text-sm text-[var(--foreground-muted)]">
          No runtime fields configured.
        </p>
      ) : (
        <div className="space-y-3">
          {Object.entries(lab.runtimeFields).map(([key, value]) => {
            if (value && typeof value === "object" && !Array.isArray(value)) {
              return (
                <fieldset
                  key={key}
                  className="space-y-2 border border-[var(--border)] rounded-md p-3"
                >
                  <legend className="px-1 text-xs font-medium text-[var(--foreground-muted)]">
                    {key}
                  </legend>
                  {Object.entries(value).map(([detailKey, detailValue]) => (
                    <label key={detailKey} className="block">
                      <span className="block text-xs font-medium mb-1">
                        {detailKey}
                      </span>
                      <input
                        value={String(detailValue ?? "")}
                        onChange={(event) =>
                          lab.setRuntimeUserDetailValue(
                            detailKey,
                            event.target.value
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-[var(--border)] rounded-md"
                      />
                    </label>
                  ))}
                </fieldset>
              );
            }

            return (
              <label key={key} className="block">
                <span className="block text-xs font-medium mb-1">{key}</span>
                <input
                  value={Array.isArray(value) ? value.join(", ") : String(value ?? "")}
                  onChange={(event) =>
                    lab.setRuntimeFieldValue(key, event.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-[var(--border)] rounded-md"
                />
              </label>
            );
          })}
        </div>
      )}
    </section>

    <section className="space-y-3">
      <h4 className="text-sm font-medium text-[var(--foreground)]">
        Generation parameters
      </h4>
      <div>
        <label className="block text-sm font-medium mb-1">Call direction</label>
        <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden">
          {callDirectionOptions.map((option) => {
            const active = lab.generationCallDirection === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => lab.setGenerationCallDirection(option.value)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
                    : "bg-transparent text-[var(--foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <NumberSettingInput
          label="Personas"
          min={1}
          max={24}
          value={lab.personaCount}
          onChange={lab.setPersonaCount}
        />
        <NumberSettingInput
          label="Scenarios per persona"
          min={1}
          max={12}
          value={lab.casesPerPersona}
          onChange={lab.setCasesPerPersona}
        />
        <NumberSettingInput
          label="Max turns per chat"
          min={2}
          max={30}
          value={lab.maxTurns}
          onChange={lab.setMaxTurns}
        />
        <NumberSettingInput
          label="Parallel jobs"
          min={1}
          max={10}
          value={lab.maxParallelScenarios}
          onChange={lab.setMaxParallelScenarios}
        />
        <NumberSettingInput
          label="Optimizer loops"
          min={1}
          max={3}
          value={lab.maxIterations}
          onChange={lab.setMaxIterations}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Pass threshold</label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0.5}
            max={1}
            step={0.01}
            value={lab.passThreshold}
            onChange={(event) =>
              lab.setPassThreshold(Number(event.target.value))
            }
            className="w-full accent-[var(--accent)]"
          />
          <span className="w-12 text-right text-sm text-[var(--foreground-muted)] tabular-nums">
            {Math.round(lab.passThreshold * 100)}%
          </span>
        </div>
      </div>
    </section>

    <section className="space-y-3">
      <h4 className="text-sm font-medium text-[var(--foreground)]">
        Languages & personas
      </h4>
      <div>
        <label className="block text-sm font-medium mb-1">Language mix</label>
        <input
          value={lab.languageMix}
          onChange={(event) => lab.setLanguageMix(event.target.value)}
          className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">
          Generation focus
        </label>
        <textarea
          rows={3}
          value={lab.instructions}
          onChange={(event) => lab.setInstructions(event.target.value)}
          className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {personaTypeOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => lab.togglePersonaType(option.value)}
            className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
              lab.personaMix.includes(option.value)
                ? "bg-[var(--accent)] text-[var(--accent-foreground)] border-[var(--accent)]"
                : "bg-transparent text-[var(--foreground)] border-[var(--border)]"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={lab.includeLanguageSwitching}
          onChange={(event) =>
            lab.setIncludeLanguageSwitching(event.target.checked)
          }
          className="h-4 w-4 accent-[var(--accent)]"
        />
        Allow language switching inside one chat
      </label>
    </section>

    <section className="space-y-2">
      <h4 className="text-sm font-medium text-[var(--foreground)]">Judge</h4>
      <button
        type="button"
        onClick={() => lab.handleProviderDefaults(lab.setJudgeProvider)}
        className="text-xs text-[var(--accent)] hover:underline"
      >
        Reset judge defaults
      </button>
    </section>
  </div>
);
